/**
 * googlePlaces.ts — Raw Google Places API calls, types, constants, and cache layer.
 *
 * Extracted from usePlaces.ts (H-203). Pure functions — no React dependencies.
 */

import { calculateDistanceMeters } from '@/utils/formatters';
import { edgeAuthHeader } from '@/lib/edgeAuth';

// ─── Core types ─────────────────────────────────────────────────────────────

/**
 * Google Places opening-hours payload (subset we read).
 * `openNow` is stripped from cached responses to avoid staleness;
 * `periods` is the stable weekly schedule.
 */
export interface OpeningHoursPeriod {
  open?: { day: number; hour: number; minute?: number; time?: string };
  close?: { day: number; hour: number; minute?: number; time?: string };
}
export interface OpeningHours {
  openNow?: boolean;
  periods?: OpeningHoursPeriod[];
  weekdayDescriptions?: string[];
}

/** Subset of the raw Google Places `place` payload we read in parsePlacesResponse. */
interface GooglePlaceRaw {
  id: string;
  displayName?: { text: string };
  formattedAddress?: string;
  rating?: number;
  userRatingCount?: number;
  photos?: { name: string }[];
  types?: string[];
  websiteUri?: string;
  location?: { latitude: number; longitude: number };
  currentOpeningHours?: OpeningHours;
  priceLevel?: string;
  editorialSummary?: { text: string };
  primaryTypeDisplayName?: { text: string };
  dineIn?: boolean;
  takeout?: boolean;
  delivery?: boolean;
  outdoorSeating?: boolean;
  reservable?: boolean;
  servesBreakfast?: boolean;
  servesLunch?: boolean;
  servesDinner?: boolean;
  servesBeer?: boolean;
  servesWine?: boolean;
  goodForChildren?: boolean;
  goodForGroups?: boolean;
  liveMusic?: boolean;
}

interface GoogleRoutingLeg {
  duration?: string;
  distanceMeters?: number;
}

interface GooglePlacesResponse {
  places?: GooglePlaceRaw[];
  routingSummaries?: { legs?: GoogleRoutingLeg[] }[];
  error?: { message?: string; status?: string };
}

export interface Place {
  id: string;
  name: string;
  displayName?: { text: string };
  formattedAddress: string;
  rating?: number;
  userRatingCount?: number;
  photos?: string[];
  types: string[];
  websiteUri?: string;
  location: { lat: number; lng: number };
  currentOpeningHours?: OpeningHours;
  priceLevel?: string;
  editorialSummary?: string;
  distanceMeters?: number;
  routingDurationSeconds?: number;
  routingDistanceMeters?: number;
  // Service attributes (free Google fields — enriches AI hooks)
  primaryTypeDisplayName?: string;
  dineIn?: boolean;
  takeout?: boolean;
  delivery?: boolean;
  outdoorSeating?: boolean;
  reservable?: boolean;
  servesBreakfast?: boolean;
  servesLunch?: boolean;
  servesDinner?: boolean;
  servesBeer?: boolean;
  servesWine?: boolean;
  goodForChildren?: boolean;
  goodForGroups?: boolean;
  liveMusic?: boolean;
}

export interface SearchResult {
  places: Place[];
  filteredClosedCount: number;
  correctedLocation?: string;
  /** True when a custom vibe was requested but the text search returned no results */
  customVibeDegraded?: boolean;
  /** The search could not reach Google at all; distinct from "nothing matched" */
  failed?: boolean;
  /** Superseded by a newer search */
  aborted?: boolean;
}

export interface ValidationResult {
  correctedName: string;
  coords: { lat: number; lng: number };
  /** Best-effort country (ISO name or formatted) returned by validateLocation when available. */
  country?: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

export const MAX_RADIUS_BY_TRANSPORT: Record<string, number> = {
  walking: 35000,
  transit: 50000,
  taxi: 50000
};

export const EXPANSION_STEPS = 5;

export const LANGUAGE_CODE_MAP: Record<string, string> = {
  en: 'en',
  fr: 'fr',
  es: 'es',
  de: 'de'
};

export const TRANSPORT_TO_TRAVEL_MODE: Record<string, string> = {
  walking: 'WALK',
  transit: 'TRANSIT',
  taxi: 'DRIVE'
};

export const MEDIUM_TOPUP_THRESHOLD = 12;

// ─── Transit station types searched when transport = 'transit' ──────────────
const TRANSIT_STATION_TYPES = [
  'transit_station', 'subway_station', 'bus_station',
  'train_station', 'light_rail_station'
];

// Walking speed used for last-mile estimates (metres per minute)
const WALK_MPM = 80;
// Average urban transit speed including acceleration/stops (metres per minute ≈ 18 km/h)
const TRANSIT_MPM = 300;
// Average wait time at a stop (minutes)
const TRANSIT_WAIT_MINS = 4;
// Average walk from alighting stop to destination (minutes)
const LAST_MILE_WALK_MINS = 5;

// ─── Place field masks ──────────────────────────────────────────────────────

const PLACE_FIELDS = [
  'places.id',
  'places.displayName',
  'places.formattedAddress',
  'places.rating',
  'places.userRatingCount',
  'places.currentOpeningHours',
  'places.photos',
  'places.types',
  'places.websiteUri',
  'places.location',
  'places.priceLevel',
  'places.editorialSummary',
  // Service attributes — free fields that enrich AI hooks
  'places.primaryTypeDisplayName',
  'places.dineIn',
  'places.takeout',
  'places.delivery',
  'places.outdoorSeating',
  'places.reservable',
  'places.servesBreakfast',
  'places.servesLunch',
  'places.servesDinner',
  'places.servesBeer',
  'places.servesWine',
  'places.goodForChildren',
  'places.goodForGroups',
  'places.liveMusic',
];

// routingSummaries requires routingParameters to be set — only request when
// a supported travelMode (DRIVE / WALK) is included in the request.
const NEARBY_FIELD_MASK = [...PLACE_FIELDS, 'routingSummaries'].join(',');
const NEARBY_FIELD_MASK_NO_ROUTING = PLACE_FIELDS.join(',');
const TEXT_SEARCH_FIELD_MASK = [...PLACE_FIELDS, 'routingSummaries'].join(',');
const TEXT_SEARCH_FIELD_MASK_NO_ROUTING = PLACE_FIELDS.join(',');

// ─── Session-level API response cache ───────────────────────────────────────
// Caches raw Google API responses for the browser session (20 min TTL).
// Prevents duplicate network calls on repeated searches (e.g. Find More stages,
// back-and-forth on InputScreen with same location/vibes).
const _apiCache = new Map<string, { data: unknown; ts: number }>();
const CACHE_TTL_MS = 20 * 60 * 1000;

function _getCached<T = unknown>(key: string): T | null {
  const entry = _apiCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) { _apiCache.delete(key); return null; }
  return entry.data as T;
}
function _setCached(key: string, data: unknown): void {
  _apiCache.set(key, { data, ts: Date.now() });
}
function _nearbyKey(
  coords: { lat: number; lng: number },
  inc: string[], exc: string[],
  radius: number, transport: string,
  rankPreference: string, languageCode: string
): string {
  return `nb:${Math.round(coords.lat * 1000)}:${Math.round(coords.lng * 1000)}:${[...inc].sort().join(',')}:${[...exc].sort().join(',')}:${Math.round(radius / 100)}:${transport}:${rankPreference}:${languageCode}`;
}
function _textKey(
  coords: { lat: number; lng: number },
  query: string, radius: number,
  transport: string, languageCode: string,
  openNow: boolean, includedType?: string,
  minRating?: number
): string {
  return `tx:${Math.round(coords.lat * 1000)}:${Math.round(coords.lng * 1000)}:${query}:${Math.round(radius / 100)}:${transport}:${languageCode}:${openNow}:${includedType ?? ''}:${Math.round((minRating ?? 0) * 10)}`;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

export function getSupabaseConfig() {
  return {
    url: import.meta.env.VITE_SUPABASE_URL as string,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
  };
}

export function parsePlacesResponse(
  data: GooglePlacesResponse,
  searchCoords: { lat: number; lng: number },
  seenPlaceIds: Set<string>,
  minRating: number,
  maxDistanceMeters?: number
): Place[] {
  const places: Place[] = (data.places || []).map((place: GooglePlaceRaw, idx: number) => {
    const placeLat = place.location?.latitude;
    const placeLng = place.location?.longitude;
    const distanceMeters = (placeLat && placeLng)
      ? calculateDistanceMeters(searchCoords.lat, searchCoords.lng, placeLat, placeLng)
      : undefined;

    const routingSummary = data.routingSummaries?.[idx];
    const routingLeg = routingSummary?.legs?.[0];

    return {
      id: place.id,
      name: place.displayName?.text || 'Unnamed Place',
      formattedAddress: place.formattedAddress || '',
      rating: place.rating,
      userRatingCount: place.userRatingCount,
      photos: place.photos?.map((p) => p.name) || [],
      types: place.types || [],
      websiteUri: place.websiteUri,
      location: { lat: placeLat, lng: placeLng },
      currentOpeningHours: place.currentOpeningHours,
      priceLevel: place.priceLevel,
      editorialSummary: place.editorialSummary?.text,
      distanceMeters,
      routingDurationSeconds: routingLeg?.duration ? parseInt(routingLeg.duration.replace('s', ''), 10) : undefined,
      routingDistanceMeters: routingLeg?.distanceMeters,
      // Service attributes
      primaryTypeDisplayName: place.primaryTypeDisplayName?.text,
      dineIn: place.dineIn,
      takeout: place.takeout,
      delivery: place.delivery,
      outdoorSeating: place.outdoorSeating,
      reservable: place.reservable,
      servesBreakfast: place.servesBreakfast,
      servesLunch: place.servesLunch,
      servesDinner: place.servesDinner,
      servesBeer: place.servesBeer,
      servesWine: place.servesWine,
      goodForChildren: place.goodForChildren,
      goodForGroups: place.goodForGroups,
      liveMusic: place.liveMusic,
    } as Place;
  });

  return places.filter((place: Place) => {
    if (seenPlaceIds.has(place.id)) return false;
    if (minRating > 0 && place.rating && place.rating < minRating) return false;
    if (maxDistanceMeters && place.distanceMeters && place.distanceMeters > maxDistanceMeters) return false;
    return true;
  });
}

// ─── Raw API calls ──────────────────────────────────────────────────────────

export async function performNearbySearch(
  searchCoords: { lat: number; lng: number },
  includedPrimaryTypes: string[],
  excludedPrimaryTypes: string[],
  radius: number,
  transport: string,
  rankPreference: string,
  languageCode: string,
  seenPlaceIds: Set<string>,
  minRating: number,
  maxDistanceMeters?: number,
  signal?: AbortSignal
): Promise<Place[]> {
  const { url } = getSupabaseConfig();
  const maxForTransport = MAX_RADIUS_BY_TRANSPORT[transport] || 50000;
  const currentRadius = Math.min(Math.round(radius), maxForTransport);

  const travelMode = TRANSPORT_TO_TRAVEL_MODE[transport];
  const includeRouting = !!travelMode && travelMode !== 'TRANSIT';

  const requestBody: Record<string, unknown> = {
    fieldMask: includeRouting ? NEARBY_FIELD_MASK : NEARBY_FIELD_MASK_NO_ROUTING,
    maxResultCount: 20,
    languageCode,
    locationRestriction: {
      circle: {
        center: { latitude: searchCoords.lat, longitude: searchCoords.lng },
        radius: currentRadius
      }
    }
  };

  if (includedPrimaryTypes.length > 0) {
    requestBody.includedPrimaryTypes = includedPrimaryTypes;
  }

  if (excludedPrimaryTypes.length > 0) {
    requestBody.excludedPrimaryTypes = excludedPrimaryTypes;
  }

  if (rankPreference) {
    requestBody.rankPreference = rankPreference;
  }

  if (includeRouting) {
    requestBody.routingParameters = {
      origin: { latitude: searchCoords.lat, longitude: searchCoords.lng },
      travelMode
    };
  }

  const cacheKey = _nearbyKey(searchCoords, includedPrimaryTypes, excludedPrimaryTypes, currentRadius, transport, rankPreference, languageCode);
  let data = _getCached<GooglePlacesResponse>(cacheKey);

  if (!data) {
    let response;
    try {
      response = await fetch(`${url}/functions/v1/llm-api/places/nearby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': await edgeAuthHeader() },
        body: JSON.stringify(requestBody),
        signal
      });
    } catch (fetchError) {
      if ((fetchError as Error).name === 'AbortError') throw fetchError;
      console.error('Network error calling Places API:', fetchError);
      throw new Error('Network error: Unable to reach Google Places API. Please check your internet connection.');
    }

    data = (await response.json()) as GooglePlacesResponse;
    if (!response.ok) {
      console.error('Places API Error:', JSON.stringify(data, null, 2));
      const errorMessage = data.error?.message || data.error?.status || JSON.stringify(data.error || data);
      throw new Error(`Places API error: ${errorMessage}`);
    }
    _setCached(cacheKey, data);
  }

  return parsePlacesResponse(data, searchCoords, seenPlaceIds, minRating, maxDistanceMeters);
}

export async function performTextSearch(
  searchCoords: { lat: number; lng: number },
  textQuery: string,
  radius: number,
  transport: string,
  languageCode: string,
  seenPlaceIds: Set<string>,
  minRating: number,
  openNow: boolean,
  includedType?: string,
  maxDistanceMeters?: number,
  signal?: AbortSignal
): Promise<Place[]> {
  const { url } = getSupabaseConfig();
  const maxForTransport = MAX_RADIUS_BY_TRANSPORT[transport] || 50000;
  const currentRadius = Math.min(Math.round(radius), maxForTransport);

  const travelMode = TRANSPORT_TO_TRAVEL_MODE[transport];
  const includeRouting = !!travelMode && travelMode !== 'TRANSIT';

  const requestBody: Record<string, unknown> = {
    fieldMask: includeRouting ? TEXT_SEARCH_FIELD_MASK : TEXT_SEARCH_FIELD_MASK_NO_ROUTING,
    textQuery,
    pageSize: 20,
    languageCode,
    locationBias: {
      circle: {
        center: { latitude: searchCoords.lat, longitude: searchCoords.lng },
        radius: currentRadius
      }
    }
  };

  if (openNow) {
    requestBody.openNow = true;
  }

  if (minRating > 0) {
    requestBody.minRating = Math.round(minRating * 2) / 2;
  }

  if (includedType) {
    requestBody.includedType = includedType;
  }

  if (includeRouting) {
    requestBody.routingParameters = {
      origin: { latitude: searchCoords.lat, longitude: searchCoords.lng },
      travelMode
    };
  }

  const cacheKey = _textKey(searchCoords, textQuery, currentRadius, transport, languageCode, openNow, includedType, minRating);
  let data = _getCached<GooglePlacesResponse>(cacheKey);

  if (!data) {
    let response;
    try {
      response = await fetch(`${url}/functions/v1/llm-api/places/text-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': await edgeAuthHeader() },
        body: JSON.stringify(requestBody),
        signal
      });
    } catch (fetchError) {
      if ((fetchError as Error).name === 'AbortError') throw fetchError;
      console.error('Network error calling Text Search API:', fetchError);
      throw new Error('Network error: Unable to reach Google Places API. Please check your internet connection.');
    }

    data = (await response.json()) as GooglePlacesResponse;
    if (!response.ok) {
      console.error('Text Search API Error:', JSON.stringify(data, null, 2));
      // 400 means Google could not use this query (callers treat that as "no matches");
      // anything else is an outage, quota or auth failure and must surface as one.
      if (response.status === 400) return [];
      throw new Error(`Places API error: ${data.error?.message || data.error?.status || response.status}`);
    }
    _setCached(cacheKey, data);
  }

  return parsePlacesResponse(data, searchCoords, seenPlaceIds, minRating, maxDistanceMeters);
}

// ─── Transit enrichment ─────────────────────────────────────────────────────

async function fetchTransitStationsNear(
  coords: { lat: number; lng: number },
  radiusMeters: number,
  languageCode: string,
  signal?: AbortSignal
): Promise<Array<{ lat: number; lng: number }>> {
  const { url } = getSupabaseConfig();
  const cacheKey = `transit:${Math.round(coords.lat * 1000)}:${Math.round(coords.lng * 1000)}:${Math.round(radiusMeters / 100)}`;
  let data = _getCached<GooglePlacesResponse>(cacheKey);

  if (!data) {
    try {
      const response = await fetch(`${url}/functions/v1/llm-api/places/nearby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': await edgeAuthHeader() },
        body: JSON.stringify({
          fieldMask: 'places.id,places.location',
          maxResultCount: 20,
          languageCode,
          locationRestriction: {
            circle: {
              center: { latitude: coords.lat, longitude: coords.lng },
              radius: Math.min(radiusMeters, 2000)
            }
          },
          includedPrimaryTypes: TRANSIT_STATION_TYPES,
          rankPreference: 'DISTANCE'
        }),
        signal
      });
      if (!response.ok) return [];
      data = (await response.json()) as GooglePlacesResponse;
      _setCached(cacheKey, data);
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      return [];
    }
  }

  return (data.places || [])
    .map((p) => ({ lat: p.location?.latitude, lng: p.location?.longitude }))
    .filter((s): s is { lat: number; lng: number } => s.lat !== undefined && s.lng !== undefined);
}

function nearestStationDistance(
  coords: { lat: number; lng: number },
  stations: Array<{ lat: number; lng: number }>
): number {
  if (stations.length === 0) return 600; // fallback: assume 600m walk if no data
  return Math.min(...stations.map(s => calculateDistanceMeters(coords.lat, coords.lng, s.lat, s.lng)));
}

export async function enrichWithTransitTimes(
  places: Place[],
  userCoords: { lat: number; lng: number },
  languageCode: string,
  signal?: AbortSignal
): Promise<Place[]> {
  if (places.length === 0) return places;

  // Single API call: fetch transit stations within 1.5 km of the user
  const userStations = await fetchTransitStationsNear(userCoords, 1500, languageCode, signal);

  // Walk time from user to their nearest transit stop
  const walkToStopMeters = nearestStationDistance(userCoords, userStations);
  const walkToStopMins = walkToStopMeters / WALK_MPM;

  return places.map(place => {
    if (!place.distanceMeters) return place;

    // Rough transit journey:
    //   walk to stop + wait + transit ride at TRANSIT_MPM + last-mile walk to POI
    const transitRideMins = place.distanceMeters / TRANSIT_MPM;
    const totalMins = walkToStopMins + TRANSIT_WAIT_MINS + transitRideMins + LAST_MILE_WALK_MINS;

    return { ...place, routingDurationSeconds: Math.round(totalMins * 60) };
  });
}

// ─── Location validation ────────────────────────────────────────────────────

export async function validateLocation(
  location: string,
  signal?: AbortSignal
): Promise<ValidationResult | null> {
  const { url } = getSupabaseConfig();
  const edgeFnHeaders = {
    'Content-Type': 'application/json',
    'Authorization': await edgeAuthHeader(),
  };

  try {
    const autocompleteResponse = await fetch(
      `${url}/functions/v1/llm-api/places/autocomplete`,
      {
        method: 'POST',
        headers: edgeFnHeaders,
        body: JSON.stringify({ input: location }),
        signal
      }
    );

    if (autocompleteResponse.ok) {
      const autocompleteData = await autocompleteResponse.json();

      if (autocompleteData.suggestions && autocompleteData.suggestions.length > 0) {
        const firstSuggestion = autocompleteData.suggestions[0];
        const placeId = firstSuggestion.placePrediction?.placeId;
        const suggestedName = firstSuggestion.placePrediction?.text?.text || location;

        if (placeId) {
          const detailsResponse = await fetch(
            `${url}/functions/v1/llm-api/places/details/${placeId}?fieldMask=location,displayName`,
            { method: 'GET', headers: edgeFnHeaders, signal }
          );

          if (detailsResponse.ok) {
            const detailsData = await detailsResponse.json();
            if (detailsData.location) {
              return {
                correctedName: detailsData.displayName?.text || suggestedName,
                coords: {
                  lat: detailsData.location.latitude,
                  lng: detailsData.location.longitude
                }
              };
            }
          }
        }
      }
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
  }

  try {
    const geocodeResponse = await fetch(
      `${url}/functions/v1/llm-api/geocode`,
      {
        method: 'POST',
        headers: edgeFnHeaders,
        body: JSON.stringify({ address: location }),
        signal
      }
    );
    const geocodeData = await geocodeResponse.json();

    if (geocodeData.status === 'OK' && geocodeData.results[0]) {
      const result = geocodeData.results[0];
      const { lat, lng } = result.geometry.location;
      const addressComponents = result.address_components;
      const cityComponent = addressComponents?.find((c: { types: string[] }) =>
        c.types.includes('locality')
      );
      const stateComponent = addressComponents?.find((c: { types: string[] }) =>
        c.types.includes('administrative_area_level_1')
      );
      const countryComponent = addressComponents?.find((c: { types: string[] }) =>
        c.types.includes('country')
      );

      return {
        correctedName: cityComponent?.long_name || stateComponent?.long_name || location,
        coords: { lat, lng },
        country: countryComponent?.long_name || null
      } as ValidationResult;
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
  }

  return null;
}
